import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Button, FormControl, InputLabel, Select,
  MenuItem, RadioGroup, FormControlLabel, Radio, TextField, CircularProgress, Box, Typography
} from '@mui/material';
import { useNotification } from '../../context/NotificationContext';
// Consider using a date/time picker library like @mui/x-date-pickers if not already installed
// import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
// import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

function ChangeFacilityStatusDialog({ open, onClose, facility, onStatusSave }) {
  const { showNotification } = useNotification();
  const [newStatus, setNewStatus] = useState('');
  const [holdOption, setHoldOption] = useState('indefinite'); // 'indefinite', 'duration', 'datetime'
  const [holdDurationValue, setHoldDurationValue] = useState(1);
  const [holdDurationUnit, setHoldDurationUnit] = useState('hours'); // 'hours', 'days', 'weeks'
  const [holdDateTime, setHoldDateTime] = useState(moment().add(1, 'hour')); // Default to 1 hour from now
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (facility) {
      setNewStatus(facility.status || 'Active');
      // Reset hold options when dialog opens or facility changes
      setHoldOption('indefinite');
      setHoldDurationValue(1);
      setHoldDurationUnit('hours');
      setHoldDateTime(moment().add(1, 'hour'));
      setError(null);
    }
  }, [facility, open]);

  // Helper function to get auth headers
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  }, []);

  const handleSave = async () => {
    const headers = getAuthHeader();
    if (!headers || !facility?._id) return;

    setLoading(true);
    setError(null);

    let payload = { status: newStatus };
    let reactivationTime = null;

    if (newStatus === 'On Hold') {
      if (holdOption === 'duration') {
        reactivationTime = moment().add(holdDurationValue, holdDurationUnit).toISOString();
      } else if (holdOption === 'datetime') {
        // Ensure the selected datetime is valid and in the future
        if (moment(holdDateTime).isValid() && moment(holdDateTime).isAfter(moment())) {
           reactivationTime = moment(holdDateTime).toISOString();
        } else {
           const errorMsg = "Please select a future date and time for reactivation.";
           setError(errorMsg);
           showNotification(errorMsg, 'error');
           setLoading(false);
           return;
        }
      }
      // If 'indefinite', reactivationTime remains null
      payload.holdReactivationTime = reactivationTime;
    }

    try {
      // Use the generic update endpoint, the controller handles status logic
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/facilities/${facility._id}`, payload, { headers });
      showNotification('Facility status updated successfully!', 'success');
      onStatusSave(response.data); // Pass updated facility back to parent
    } catch (err) {
      console.error("Error updating facility status:", err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to update status.";
      setError(errorMessage);
      showNotification(`Error updating facility status: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Basic check for valid date/time picker input (if using TextField)
  const isDateTimeValid = moment(holdDateTime).isValid();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change Status for {facility?.facilityName}</DialogTitle>
      <DialogContent>
        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
        <FormControl fullWidth margin="normal">
          <InputLabel>New Status</InputLabel>
          <Select
            value={newStatus}
            label="New Status"
            onChange={(e) => setNewStatus(e.target.value)}
          >
            {/* Define allowed statuses */}
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
            <MenuItem value="On Hold">On Hold</MenuItem>
            {/* Pending/Rejected might be handled elsewhere */}
            {facility?.status === 'Pending' && <MenuItem value="Pending" disabled>Pending</MenuItem>}
             {facility?.status === 'Rejected' && <MenuItem value="Rejected" disabled>Rejected</MenuItem>}
          </Select>
        </FormControl>

        {newStatus === 'On Hold' && (
          <Box mt={2}>
            <Typography variant="subtitle1" gutterBottom>Hold Options</Typography>
            <RadioGroup row value={holdOption} onChange={(e) => setHoldOption(e.target.value)}>
              <FormControlLabel value="indefinite" control={<Radio />} label="Indefinite" />
              <FormControlLabel value="duration" control={<Radio />} label="Duration" />
              <FormControlLabel value="datetime" control={<Radio />} label="Specific Date/Time" />
            </RadioGroup>

            {holdOption === 'duration' && (
              <Box display="flex" alignItems="center" mt={1}>
                <TextField
                  type="number"
                  value={holdDurationValue}
                  onChange={(e) => setHoldDurationValue(parseInt(e.target.value) || 1)}
                  size="small"
                  InputProps={{ inputProps: { min: 1 } }}
                  sx={{ width: '80px', mr: 1 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={holdDurationUnit}
                    onChange={(e) => setHoldDurationUnit(e.target.value)}
                  >
                    <MenuItem value="hours">Hour(s)</MenuItem>
                    <MenuItem value="days">Day(s)</MenuItem>
                    <MenuItem value="weeks">Week(s)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {holdOption === 'datetime' && (
              <Box mt={1}>
                {/* Replace TextField with a proper DateTimePicker component for better UX */}
                {/* Example using TextField (requires manual validation) */}
                 <TextField
                   label="Reactivation Date & Time"
                   type="datetime-local"
                   value={moment(holdDateTime).format('YYYY-MM-DDTHH:mm')} // Format for input
                   onChange={(e) => setHoldDateTime(moment(e.target.value))}
                   fullWidth
                   InputLabelProps={{ shrink: true }}
                   error={!isDateTimeValid}
                   helperText={!isDateTimeValid ? "Invalid date/time format" : ""}
                 />
                 {/* Example using @mui/x-date-pickers (install required) */}
                 {/*
                 <LocalizationProvider dateAdapter={AdapterMoment}>
                     <DateTimePicker
                         label="Reactivation Date & Time"
                         value={holdDateTime}
                         onChange={(newValue) => setHoldDateTime(newValue)}
                         renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                         minDateTime={moment().add(1, 'minute')} // Prevent selecting past dates
                     />
                 </LocalizationProvider>
                 */}
              </Box>
            )}
          </Box>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSave} color="primary" disabled={loading || (holdOption === 'datetime' && !isDateTimeValid)}>
          {loading ? <CircularProgress size={24} /> : 'Save Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChangeFacilityStatusDialog;
